import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight, Tag, Loader2, Check, X } from 'lucide-react';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SHIPPING_RATE = 9.95;

export const Cart = () => {
  const { cart, removeFromCart, updateQuantity, getCartTotal } = useCart();
  const navigate = useNavigate();
  
  // Discount state
  const [discountCode, setDiscountCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [discountLoading, setDiscountLoading] = useState(false);
  const [discountError, setDiscountError] = useState('');

  // Load applied discount from sessionStorage
  useEffect(() => {
    const savedDiscount = sessionStorage.getItem('goat_discount');
    if (savedDiscount) {
      const parsed = JSON.parse(savedDiscount);
      // Recalculate discount amount based on current cart
      if (parsed.code) {
        validateAndApplyDiscount(parsed.code, true);
      }
    }
  }, [cart]);

  const validateAndApplyDiscount = async (code, silent = false) => {
    if (!code.trim()) {
      if (!silent) setDiscountError('Please enter a code');
      return;
    }

    const subtotal = getCartTotal();
    if (subtotal === 0) {
      if (!silent) setDiscountError('Add items to cart first');
      return;
    }

    setDiscountLoading(true);
    setDiscountError('');

    try {
      const response = await axios.post(`${API}/discount/validate`, {
        code: code.trim(),
        subtotal: subtotal
      });

      const discount = {
        code: response.data.code,
        amount: response.data.discountAmount,
        description: response.data.description
      };

      setAppliedDiscount(discount);
      sessionStorage.setItem('goat_discount', JSON.stringify(discount));
      
      if (!silent) {
        toast.success(`Discount applied: ${discount.description}`);
      }
      setDiscountCode('');
    } catch (error) {
      if (!silent) {
        setDiscountError(error.response?.data?.detail || 'Invalid discount code');
      }
      // Clear invalid discount
      setAppliedDiscount(null);
      sessionStorage.removeItem('goat_discount');
    } finally {
      setDiscountLoading(false);
    }
  };

  const handleApplyDiscount = () => {
    if (appliedDiscount && discountCode.trim().toUpperCase() === appliedDiscount.code) {
      setDiscountError('This code is already applied');
      return;
    }
    validateAndApplyDiscount(discountCode);
  };

  const handleRemoveDiscount = () => {
    setAppliedDiscount(null);
    sessionStorage.removeItem('goat_discount');
    toast.success('Discount removed');
  };

  const subtotal = getCartTotal();
  const discountAmount = appliedDiscount ? appliedDiscount.amount : 0;
  const shipping = cart.length > 0 ? SHIPPING_RATE : 0;
  const total = subtotal - discountAmount + shipping;

  // Navigate to checkout with discount info
  const handleCheckout = () => {
    navigate('/checkout');
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-white py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <ShoppingBag className="w-16 h-16 mx-auto text-zinc-300 mb-6" />
            <h1 className="font-heading font-black text-3xl uppercase tracking-tight mb-4">
              Your Cart is Empty
            </h1>
            <p className="text-zinc-500 mb-8">
              Looks like you haven't added any items yet.
            </p>
            <Link 
              to="/shop" 
              className="btn-primary inline-flex items-center gap-2"
              data-testid="continue-shopping-empty"
            >
              Start Shopping
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-[#09090b] text-white py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h1 
            className="font-heading font-black text-3xl md:text-4xl uppercase tracking-tight"
            data-testid="cart-title"
          >
            Your Cart
          </h1>
          <p className="text-zinc-400 mt-2">
            {cart.length} {cart.length === 1 ? 'item' : 'items'}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Cart Items */}
          <div className="lg:col-span-2" data-testid="cart-items">
            <div className="space-y-6">
              {cart.map((item, index) => (
                <motion.div
                  key={`${item.productId}-${item.size}-${item.colour}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  className="flex gap-6 pb-6 border-b border-zinc-200"
                  data-testid={`cart-item-${item.productId}`}
                >
                  {/* Image */}
                  <Link to={`/product/${item.productId}`} className="shrink-0">
                    <div className="w-24 h-24 md:w-32 md:h-32 bg-zinc-100 overflow-hidden">
                      <img
                        src={item.imageUrl}
                        alt={item.productName}
                        className="w-full h-full object-cover hover:scale-105 transition-transform"
                      />
                    </div>
                  </Link>

                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <Link to={`/product/${item.productId}`}>
                      <h3 className="font-heading font-bold text-base uppercase tracking-tight hover:text-[#00ff9d] transition-colors">
                        {item.productName}
                      </h3>
                    </Link>
                    
                    <div className="text-zinc-500 text-sm mt-1 space-x-3">
                      {item.size && <span>Size: {item.size}</span>}
                      {item.colour && <span>Colour: {item.colour}</span>}
                    </div>

                    <p className="font-bold mt-2">
                      ${item.price.toFixed(2)} <span className="text-zinc-400 text-sm font-normal">AUD</span>
                    </p>

                    {/* Quantity & Remove */}
                    <div className="flex items-center justify-between mt-4">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => updateQuantity(item.productId, item.size, item.colour, item.quantity - 1)}
                          className="w-8 h-8 border border-zinc-200 flex items-center justify-center hover:border-black transition-colors"
                          data-testid={`decrease-${item.productId}`}
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="font-bold w-6 text-center">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.productId, item.size, item.colour, item.quantity + 1)}
                          className="w-8 h-8 border border-zinc-200 flex items-center justify-center hover:border-black transition-colors"
                          data-testid={`increase-${item.productId}`}
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>

                      <button
                        onClick={() => removeFromCart(item.productId, item.size, item.colour)}
                        className="text-zinc-400 hover:text-[#ff4d6d] transition-colors"
                        data-testid={`remove-${item.productId}`}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  {/* Line Total (Desktop) */}
                  <div className="hidden md:block text-right">
                    <p className="font-bold text-lg">
                      ${(item.price * item.quantity).toFixed(2)}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>

            <Link 
              to="/shop" 
              className="inline-flex items-center gap-2 text-zinc-600 hover:text-black transition-colors mt-6"
              data-testid="continue-shopping"
            >
              <ArrowRight className="w-4 h-4 rotate-180" />
              Continue Shopping
            </Link>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div 
              className="bg-zinc-50 p-6 md:p-8 sticky top-24"
              data-testid="order-summary"
            >
              <h2 className="font-heading font-bold text-xl uppercase tracking-tight mb-6">
                Order Summary
              </h2>

              {/* Discount Code Input */}
              <div className="mb-6 pb-6 border-b border-zinc-200">
                <label className="font-bold text-xs uppercase tracking-wider text-zinc-600 mb-2 block">
                  Discount Code
                </label>
                
                {appliedDiscount ? (
                  <div 
                    className="flex items-center justify-between bg-[#00ff9d]/10 border border-[#00ff9d]/30 p-3"
                    data-testid="applied-discount"
                  >
                    <div className="flex items-center gap-2">
                      <Tag className="w-4 h-4 text-[#00cc7d]" />
                      <span className="font-bold text-sm">{appliedDiscount.code}</span>
                      <Check className="w-4 h-4 text-[#00cc7d]" />
                    </div>
                    <button
                      onClick={handleRemoveDiscount}
                      className="text-zinc-400 hover:text-[#ff4d6d] transition-colors"
                      data-testid="remove-discount"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      placeholder="Enter code"
                      value={discountCode}
                      onChange={(e) => {
                        setDiscountCode(e.target.value.toUpperCase());
                        setDiscountError('');
                      }}
                      className="h-10 uppercase"
                      data-testid="discount-code-input"
                    />
                    <Button
                      onClick={handleApplyDiscount}
                      disabled={discountLoading}
                      variant="outline"
                      className="h-10 px-4 font-bold uppercase text-xs"
                      data-testid="apply-discount-btn"
                    >
                      {discountLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        'Apply'
                      )}
                    </Button>
                  </div>
                )}
                {discountError && (
                  <p className="text-[#ff4d6d] text-xs mt-2" data-testid="discount-error">
                    {discountError}
                  </p>
                )}
              </div>

              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-zinc-600">Subtotal</span>
                  <span className="font-medium" data-testid="cart-subtotal">
                    ${subtotal.toFixed(2)}
                  </span>
                </div>
                
                {appliedDiscount && (
                  <div className="flex justify-between text-[#00cc7d]">
                    <span>Discount ({appliedDiscount.code})</span>
                    <span className="font-medium" data-testid="cart-discount">
                      -${discountAmount.toFixed(2)}
                    </span>
                  </div>
                )}
                
                <div className="flex justify-between">
                  <span className="text-zinc-600">Shipping</span>
                  <span className="font-medium" data-testid="cart-shipping">
                    ${shipping.toFixed(2)}
                  </span>
                </div>
                <div className="border-t border-zinc-200 pt-4 flex justify-between">
                  <span className="font-bold">Total</span>
                  <span className="font-bold text-xl" data-testid="cart-total">
                    ${total.toFixed(2)} <span className="text-zinc-400 text-sm font-normal">AUD</span>
                  </span>
                </div>
              </div>

              <Button
                onClick={handleCheckout}
                className="w-full h-14 mt-8 bg-[#09090b] text-white hover:bg-[#00ff9d] hover:text-black font-bold uppercase tracking-wider rounded-full transition-all"
                data-testid="checkout-btn"
              >
                Proceed to Checkout
              </Button>

              <p className="text-zinc-500 text-xs text-center mt-4">
                Secure checkout powered by Stripe
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
